import React, { JSX, useContext } from 'react';
import classNames from 'classnames';
import { configurationContext } from '../../../../contexts/configuration';
import { styled } from '@mui/material/styles';
import { scrollbar } from 'src/theme/mixins';
import { VirtualizedMessages } from './VirtualizedMessages';
import { ReversedMessages } from './ReversedMessages';

type IMessageListProps = Omit<
	Omit<JSX.IntrinsicElements['div'], 'ref'>,
	'children'
>;

/**
 * Root messages list component. Reads the `virtualized_messages_list`
 * configuration flag and delegates rendering to either
 * {@link VirtualizedMessages} or {@link ReversedMessages}.
 *
 * The `legacy` query-string parameter acts as an escape hatch to force the
 * legacy renderer regardless of the configuration value, useful for debugging
 * or when the virtualised renderer shows artefacts.
 */
export const MessagesList = styled(
	React.memo(({ className, ...props }: IMessageListProps) => {
		const { virtualized_messages_list } = useContext(configurationContext);
		const isVirtualized =
			(virtualized_messages_list === true ||
				window.location.search.includes('virtualized')) &&
			!window.location.search.includes('legacy');

		return (
			<div
				{...props}
				className={classNames(
					'messages-list',
					isVirtualized ? 'messages-list--virtual' : 'messages-list--normal',
					className,
				)}
			>
				{isVirtualized ? <VirtualizedMessages /> : <ReversedMessages />}
			</div>
		);
	}),
	{ label: 'MessagesList', target: 'MessagesList-root' },
)({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	overflow: 'hidden',
	...scrollbar(),
});
